<div
  role="treegrid"
  [attr.aria-multiselectable]="canSelectRows || null"
  class="GridTable"
  [style.width.px]="totalWidth"
  [style.--grid-head-height.px]="headHeight"
  [style.--grid-row-height.px]="standardRowHeight"
  [style.--grid-template-columns]="gridTemplateColumns"
  [hidden]="!gridTemplateColumns"
>
  <header
    role="rowgroup"
    [observe]="['offsetHeight']"
    (observeResize)="onHeadResize($event)"
  >
    <div role="row" class="GridTable-row" *ngIf="columnGroupsWithSpan">
      <div
        *ngFor="let groupWithSpan of columnGroupsWithSpan"
        role="columnheader"
        [attr.aria-colspan]="
          groupWithSpan.group?.open
            ? groupWithSpan.openColspan
            : groupWithSpan.closedColspan
        "
        [attr.aria-expanded]="
          groupWithSpan.closedColspan &&
          groupWithSpan.openColspan > groupWithSpan.closedColspan
            ? groupWithSpan.group?.open
            : null
        "
        [class.GridTable--fixedLeft]="groupWithSpan.leftOffset != null"
        [class.GridTable--fixedRight]="groupWithSpan.rightOffset != null"
        [style.--left-offset.px]="groupWithSpan.leftOffset"
        [style.--left-fixed.px]="groupWithSpan.leftFixed"
        [style.--left-non-fixed.px]="
          groupWithSpan.group?.open ? groupWithSpan.leftNonFixed : null
        "
        [style.--right-offset.px]="groupWithSpan.rightOffset"
        [style.--right-fixed.px]="groupWithSpan.rightFixed"
        [style.--right-non-fixed.px]="
          groupWithSpan.group?.open ? groupWithSpan.rightNonFixed : null
        "
      >
        <ng-container *ngIf="groupWithSpan.group as columnGroup">
          <button
            data-dd-action-name="grid-table.toggle-column-group-button"
            *ngIf="
              groupWithSpan.closedColspan &&
                groupWithSpan.openColspan > groupWithSpan.closedColspan;
              else GroupLabel
            "
            type="button"
            class="GridTable-headerButton"
            (click)="toggleColumnGroup(columnGroup)"
          >
            <div>
              <ng-container
                *ngTemplateOutlet="
                  columnGroup.headerTemplate || NoGroupHeaderTemplate
                "
                [style.left.px]="groupWithSpan.leftOffset"
              ></ng-container>
              <ng-template #NoGroupHeaderTemplate>{{
                columnGroup.headerName
              }}</ng-template>
            </div>
            <div class="text-body-secondary">
              <i-lucide
                [name]="columnGroup.open ? 'chevron-left' : 'chevron-right'"
              ></i-lucide>
            </div>
          </button>
          <ng-template #GroupLabel>
            <span class="GridTable-headerLabel">
              <div>
                <ng-container
                  *ngTemplateOutlet="
                    columnGroup.headerTemplate || NoGroupHeaderTemplate
                  "
                  [style.left.px]="groupWithSpan.leftOffset"
                ></ng-container>
                <ng-template #NoGroupHeaderTemplate>{{
                  columnGroup.headerName
                }}</ng-template>
              </div>
              <div></div>
            </span>
          </ng-template>
        </ng-container>
      </div>
    </div>
    <div role="row" class="GridTable-row">
      <div
        *ngFor="let column of visibleColumns; let i = index"
        role="columnheader"
        [ngClass]="column.cellClass"
        [class.GridTable--fixedLeft]="column.fixed === 'left'"
        [class.GridTable--fixedRight]="column.fixed === 'right'"
        [style.--left-offset.px]="offsets.get(column)?.left"
        [style.--left-fixed.px]="
          column.fixed &&
          (column.group?.open ? openColumnWidths : closedColumnWidths)[i]
        "
        [style.--right-offset.px]="offsets.get(column)?.right"
        [style.--right-fixed.px]="
          column.fixed &&
          (column.group?.open ? openColumnWidths : closedColumnWidths)[i]
        "
        [hidden]="
          column.hidden ||
          (hideNonAggColumns && !column.isHeader && !column.hasAggregate)
        "
        [ngbTooltip]="column.headerTooltip"
      >
        <ng-container
          *ngIf="column.headerTemplate as headerTemplate; else NoHeaderTemplate"
          [ngTemplateOutlet]="headerTemplate"
        ></ng-container>
        <ng-template #NoHeaderTemplate>
          <div
            *ngIf="column.headerHtml as html"
            class="flex-grow-1"
            [innerHTML]="html"
          ></div>
          <ng-container *ngIf="!column.headerHtml">{{
            column.headerName
          }}</ng-container>
        </ng-template>
      </div>
    </div>
  </header>
  <section role="rowgroup" class="GridTable-body">
    <ng-container *ngIf="refreshing && !groupedData?.length; else BodyRows">
      <ng-container
        *ngTemplateOutlet="
          Row;
          context: {
            $implicit: { state: 'LOADING' },
            index: 0,
            count: 1,
            indent: -1,
          }
        "
      ></ng-container>
    </ng-container>
    <ng-template #BodyRows>
      <ng-container
        *ngTemplateOutlet="
          Rows;
          context: {
            $implicit: groupedData,
            indent: 0,
            ancestors: [],
          }
        "
      ></ng-container>
    </ng-template>
  </section>
  <footer *ngIf="showFooter" role="rowgroup">
    <div role="row" class="GridTable-row">
      <div
        *ngFor="let column of visibleColumns; let i = index"
        [attr.role]="column.isHeader ? 'rowheader' : 'gridcell'"
        [ngClass]="column.cellClass"
        [class.GridTable--fixedLeft]="column.fixed === 'left'"
        [class.GridTable--fixedRight]="column.fixed === 'right'"
        [style.left.px]="offsets.get(column)?.left"
        [style.right.px]="offsets.get(column)?.right"
        [hidden]="
          column.hidden ||
          (hideNonAggColumns && !column.isHeader && !column.hasAggregate)
        "
      >
        <ng-container *ngIf="column.hasAggregate">
          <ng-container
            *ngTemplateOutlet="
              Cell;
              context: {
                $implicit: column.aggregate(groupedData),
                column: column,
              }
            "
          ></ng-container>
        </ng-container>
      </div>
    </div>
  </footer>
</div>

<ng-template #Rows let-data let-indent="indent" let-ancestors="ancestors">
  <cdk-virtual-scroll-viewport
    *ngIf="data && data.length > 5; else Loop"
    [config]="virtualizeConfig"
    class="GridTable-rows"
  >
    <!-- Because of the nested virtual for we don't want to cache templates -->
    <ng-container
      *cdkVirtualFor="
        let row of data;
        trackBy: trackBy;
        templateCacheSize: 0;
        let index = index;
        let count = count
      "
    >
      <ng-container
        *ngTemplateOutlet="
          RowOrGroup;
          context: {
            $implicit: row,
            index,
            count,
            indent,
            ancestors,
          }
        "
      ></ng-container>
    </ng-container>
  </cdk-virtual-scroll-viewport>
  <ng-template #Loop>
    <ng-container
      *ngFor="
        let row of data;
        trackBy: trackBy;
        let index = index;
        let count = count
      "
    >
      <ng-container
        *ngTemplateOutlet="
          RowOrGroup;
          context: {
            $implicit: row,
            index,
            count,
            indent,
            ancestors,
          }
        "
      ></ng-container>
    </ng-container>
  </ng-template>
</ng-template>

<ng-template
  #RowOrGroup
  let-row
  let-index="index"
  let-count="count"
  let-indent="indent"
  let-ancestors="ancestors"
>
  <ng-container *ngIf="row.key != null; else SimpleRow">
    <ng-container
      *ngTemplateOutlet="
        RowGroup;
        context: {
          $implicit: row,
          index,
          count,
          indent,
          ancestors,
        }
      "
    ></ng-container>
  </ng-container>
  <ng-template #SimpleRow>
    <ng-container
      *ngTemplateOutlet="
        Row;
        context: {
          $implicit: row,
          index,
          count,
          indent,
        }
      "
    ></ng-container>
  </ng-template>
</ng-template>

<ng-template
  #RowGroup
  let-row
  let-index="index"
  let-count="count"
  let-indent="indent"
  let-ancestors="ancestors"
>
  <section role="none" class="GridTable-rowGroup">
    <header
      *ngIf="row.key"
      role="row"
      [attr.aria-level]="indent + 1"
      [attr.aria-setsize]="count"
      [attr.aria-posinset]="index + 1"
      [attr.aria-expanded]="row.open"
      [ngClass]="row.open ? row.openRowCssClasses : row.closedRowCssClasses"
      class="GridTable-row"
    >
      <ng-container *ngFor="let column of visibleColumns; let i = index">
        <div
          [attr.role]="column.isHeader ? 'rowheader' : 'gridcell'"
          [ngClass]="column.cellClass"
          [class.GridTable--fixedLeft]="column.fixed === 'left'"
          [class.GridTable--fixedRight]="column.fixed === 'right'"
          [class.GridTable-loading]="
            !column.isHeader &&
            refreshing &&
            (row.groupData
              ? column.valueGetter(row.groupData) != null
              : column.hasAggregate)
          "
          [style.left.px]="offsets.get(column)?.left"
          [style.right.px]="offsets.get(column)?.right"
          [hidden]="
            column.hidden ||
            (hideNonAggColumns && !column.isHeader && !column.hasAggregate)
          "
        >
          <button
            data-dd-action-name="grid-table.toggle-row-group-button"
            *ngIf="column.isHeader; else HeadNotFirst"
            type="button"
            class="GridTable-headRowButton"
            [disabled]="row.count === 0"
            (click)="toggleRowGroup(row)"
          >
            <i-lucide
              [name]="row.open ? 'chevron-down' : 'chevron-right'"
              [hidden]="row.count === 0"
            ></i-lucide>
            <div>
              <ng-container
                *ngTemplateOutlet="
                  GroupDisplay;
                  context: {
                    $implicit: row,
                    showCount: true,
                  }
                "
              ></ng-container>
            </div>
          </button>

          <ng-template #HeadNotFirst>
            <ng-container
              *ngIf="
                !refreshing &&
                !row.groupData &&
                !row.open &&
                column.hasAggregate
              "
            >
              <ng-container
                *ngTemplateOutlet="
                  Cell;
                  context: {
                    $implicit: column.aggregate(row.children),
                    row: row,
                    column: column,
                  }
                "
              ></ng-container>
            </ng-container>
            <ng-container *ngIf="!refreshing && row.groupData">
              <ng-container
                *ngTemplateOutlet="
                  Cell;
                  context: {
                    $implicit: column.valueGetter(row.groupData),
                    data: row.groupData,
                    row: row,
                    column: column,
                  }
                "
              ></ng-container>
            </ng-container>
          </ng-template>
        </div>

        <ng-template #GroupDisplay let-row let-showCount="showCount">
          <ng-container *ngIf="!row.groupData">
            <span>{{ row.key }}</span>
            <span
              *ngIf="showCount && row.count"
              class="badge rounded-pill bg-light text-dark ms-2"
              >{{ row.count | number }}</span
            >
          </ng-container>
          <ng-container *ngIf="row.groupData">
            <ng-container
              *ngTemplateOutlet="
                Cell;
                context: {
                  $implicit: column.valueGetter(row.groupData),
                  data: row.groupData,
                  row: row,
                  column: column,
                }
              "
            ></ng-container>
          </ng-container>
        </ng-template>
      </ng-container>
    </header>
    <ng-container *ngIf="row.open">
      <ng-container
        *ngTemplateOutlet="
          Rows;
          context: {
            $implicit: row.children,
            indent: row.key ? indent + 1 : indent,
            ancestors: row.key ? ancestors.concat([row]) : ancestors,
          }
        "
      ></ng-container>
    </ng-container>
    <footer
      *ngIf="row.key && showGroupFooter"
      role="row"
      [attr.aria-level]="indent + 1"
      class="GridTable-row"
      [hidden]="!row.open"
    >
      <div
        *ngFor="let column of visibleColumns; let i = index"
        [attr.role]="column.isHeader ? 'rowheader' : 'gridcell'"
        [ngClass]="column.cellClass"
        [class.GridTable--fixedLeft]="column.fixed === 'left'"
        [class.GridTable--fixedRight]="column.fixed === 'right'"
        [style.left.px]="offsets.get(column)?.left"
        [style.right.px]="offsets.get(column)?.right"
        [hidden]="column.hidden"
      >
        <div class="GridTable-cellInner">
          <strong *ngIf="column.isHeader; else FootNotFirst">
            {{ row.key }}
          </strong>
          <ng-template #FootNotFirst>
            <ng-container *ngIf="!refreshing && column.hasAggregate">
              <ng-container
                *ngTemplateOutlet="
                  Cell;
                  context: {
                    $implicit: column.aggregate(row.children),
                    row: row,
                    column: column,
                  }
                "
              ></ng-container>
            </ng-container>
          </ng-template>
        </div>
      </div>
    </footer>
  </section>
</ng-template>

<ng-template
  #Row
  let-row
  let-index="index"
  let-count="count"
  let-indent="indent"
>
  <article
    role="row"
    [attr.aria-level]="indent + 1"
    [attr.aria-setsize]="count"
    [attr.aria-posinset]="index + 1"
    [attr.aria-selected]="canSelectRows ? row.selected : null"
    [attr.is-loaded]="row.state === 'LOADED'"
    class="GridTable-row"
    [class.is-even]="index % 2 === 0"
    [class.is-odd]="index % 2 === 1"
    [ngClass]="row.rowCssClasses"
    [style.height.px]="getRowHeight(row)"
  >
    <ng-container *ngIf="row.state === 'LOADED' && !refreshing; else Loading">
      <div
        *ngFor="let column of visibleColumns"
        [attr.role]="column.isHeader ? 'rowheader' : 'gridcell'"
        [ngClass]="column.cellClass"
        [class.GridTable--fixedLeft]="column.fixed === 'left'"
        [class.GridTable--fixedRight]="column.fixed === 'right'"
        [style.left.px]="offsets.get(column)?.left"
        [style.right.px]="offsets.get(column)?.right"
        [hidden]="column.hidden"
      >
        <div class="GridTable-cellInner">
          <ng-container
            *ngTemplateOutlet="
              Cell;
              context: {
                $implicit: column.valueGetter(row.data),
                data: row.data,
                row: row,
                column: column,
              }
            "
          ></ng-container>
        </div>
      </div>
    </ng-container>
    <ng-template #Loading>
      <div
        role="gridcell"
        [attr.aria-colspan]="loadingColspan"
        class="GridTable-loading py-0"
        [class.GridTable--errored]="row.state === 'ERROR'"
      >
        <div class="GridTable-cellInner">
          <button
            data-dd-action-name="grid-table.retry-button"
            *ngIf="row.state === 'ERROR'"
            type="button"
            class="btn btn-link text-danger w-100"
            (click)="retryFailed(row)"
          >
            Retry
          </button>
        </div>
      </div>
    </ng-template>
  </article>
</ng-template>

<ng-template #Cell let-value let-column="column" let-data="data" let-row="row">
  <ng-container *ngIf="column.cellTemplate; else NoCellTemplate">
    <ng-container
      *ngTemplateOutlet="
        column.cellTemplate;
        context: {
          $implicit: column.valueFormatter
            ? column.valueFormatter(value)
            : value,
          data: data,
          row: row,
        }
      "
    ></ng-container>
  </ng-container>
  <ng-template #NoCellTemplate>
    {{ column.valueFormatter ? column.valueFormatter(value) : value }}
  </ng-template>
</ng-template>

<ng-template #Select let-row="row">
  <input
    data-dd-action-name="grid-table.select-row-checkbox"
    type="checkbox"
    aria-label="Select"
    [checked]="row.selected"
    [indeterminate]="row.selected === 'INDETERMINATE'"
    [disabled]="row.key == null && !row.data"
    (change)="onSelectedRowToggled(row)"
  />
</ng-template>

<ng-template #SelectHeader>
  <input
    data-dd-action-name="grid-table.select-all-checkbox"
    type="checkbox"
    aria-label="Select All"
    [checked]="allRowsSelected"
    [indeterminate]="allRowsSelected === 'INDETERMINATE'"
    [disabled]="!groupedData.length"
    (change)="onSelectedRowsToggled()"
  />
</ng-template>
